import { arrayMove, SortableContext } from "@dnd-kit/sortable";
import { clone } from "ramda";
import { useContext, useEffect, useState } from "react";
import { ActionButton, ActionButtonSize, ColorButton, HeaderBar, HStack, SymbolIcon, TooltipIntent } from "widgets";
import type { FileDefinitionType } from "widgets/utils";

import { type FileBrowserProps } from "@/modules/files/components/FileBrowser.js";
import { FilePickerControlledDialog } from "@/modules/files/components/FilePickerControlledDialog.js";
import type { BrowsingProjectFileFragment } from "@/modules/files/graphql/files-queries.gql.ts";
import { getParentPath } from "@/modules/files/utils/file.ts";
import { COLOR } from "@/settings/colors-settings.ts";
import { TooltipController } from "@/ui-modules/core/overlays/components/OverlayControllers.tsx";
import { DndActiveContext } from "@/ui-modules/genome-viewer/GenomeViewerInteractiveSurface.tsx";
import { useGenomeViewerFileSuggestionsContext } from "@/ui-modules/genome-viewer/hooks/useGenomeViewerFileSuggestionsContext.ts";

import { ContentPlaceholder } from "../../core/ContentPlaceholder.js";
import { SortableItem, SortableList } from "../../core/forms/Sortable.js";
import style from "../../core/forms/styles/Sortable.module.scss";

export interface ITrackFilesSelectionProps {
  selectedFiles: FileSelection[];
  setSelectedFiles: (files: FileSelection[]) => void;
  isSelectableFilter: FileBrowserProps["isSelectableFilter"];
  baseFilterExtension: FileDefinitionType;
  baseFilterHint: string;
  defaultColor: string;
}

export interface FileSelection {
  file: BrowsingProjectFileFragment;
  color: string;
}

const TrackFilesSelection = (props: ITrackFilesSelectionProps) => {
  const [open, setOpen] = useState(false);
  const handleColorModification = (file: FileSelection, color: string) => {
    props.setSelectedFiles(
      props.selectedFiles.map((selectedFile: FileSelection) => {
        return selectedFile.file.id === file.file.id ? { ...selectedFile, color: color } : selectedFile;
      }),
    );
  };

  const handleRemoveFiles = (removedItem: FileSelection) => {
    props.setSelectedFiles(props.selectedFiles.filter((bf) => bf.file.id !== removedItem.file.id));
  };

  const handleAddFiles = (files: BrowsingProjectFileFragment[]) => {
    if (files.length > 0) {
      // this piece of code was useful for automatically choosing another color
      // const usedColors: Array<string> = this.state.selectedBAMFiles.map((f) => f.color);

      const newSelection: FileSelection[] = files
        // remove duplicates
        .filter((file) => !props.selectedFiles.find((s) => s.file.id === file.id))
        .map((file) => {
          // this piece of code was useful for automatically choosing another unique color
          // const newColor = chooseUniqueColor(settings.CHART_COLOR_SET, usedColors);
          // usedColors.push(newColor);
          return {
            file: file,
            color: props.defaultColor,
          } satisfies FileSelection;
        });
      props.setSelectedFiles([...props.selectedFiles].concat(newSelection));
    }
    return Promise.resolve();
  };

  const getGenomeViewerFileSuggestionsContext = useGenomeViewerFileSuggestionsContext();

  const { setDndActive } = useContext(DndActiveContext);
  useEffect(() => {
    setDndActive(false);
    return () => {
      setDndActive(true);
    };
  }, [setDndActive]);

  const [preventDrag, setPreventDrag] = useState(false);

  return (
    <>
      <HeaderBar
        title="Data Selection"
        actions={[
          <ActionButton
            onPress={() => setOpen(true)}
            size={ActionButtonSize.Small}
            label={`Add ${props.baseFilterExtension} Files`}
            key="track-file-data-selection"
          />,
        ]}
      />
      <div>
        {props.selectedFiles.length === 0 ? (
          <ContentPlaceholder>No files have been selected</ContentPlaceholder>
        ) : (
          <div className="form-group">
            <SortableList
              id="dataseries-list"
              onDragEnd={({ active, over }) => {
                const selectables: FileSelection[] = clone(props.selectedFiles);
                if (over) {
                  props.setSelectedFiles(
                    arrayMove<FileSelection>(
                      selectables,
                      active.data.current?.sortable.index,
                      over.data.current?.sortable.index,
                    ),
                  );
                }
              }}
            >
              <SortableContext
                items={props.selectedFiles.map((fileSelection) => fileSelection.file.id)}
                disabled={preventDrag}
              >
                {props.selectedFiles.map((fileSelection: FileSelection, index: number) => (
                  <SortableItem
                    key={`item-${index}`}
                    index={index}
                    id={fileSelection.file.id}
                    onRemove={() => handleRemoveFiles(fileSelection)}
                  >
                    <HStack>
                      {fileSelection.file.hidden || fileSelection.file.deleted ? (
                        <TooltipController
                          content={
                            fileSelection.file.hidden
                              ? "This file is not accessible to you."
                              : "This file has been deleted."
                          }
                          intent={TooltipIntent.Danger}
                        >
                          {(tooltipTriggerProps) => (
                            <SymbolIcon
                              icon={fileSelection.file.deleted ? "delete" : "security"}
                              style={{ color: COLOR.DARKISH_PINK.BORDER }}
                              size={16}
                              domProps={tooltipTriggerProps}
                            />
                          )}
                        </TooltipController>
                      ) : (
                        <ColorButton
                          defaultColor={fileSelection.color || props.defaultColor}
                          onApply={(color) => handleColorModification(fileSelection, color)}
                          onOpen={() => setPreventDrag(true)}
                          onClose={() => setPreventDrag(false)}
                        />
                      )}

                      <span className={style.label}>
                        {fileSelection.file.hidden ? `[Restricted Content]` : fileSelection.file.fileName}
                        <div className={style.sub_label}>{getParentPath(fileSelection.file.humanReadablePath)}</div>
                      </span>
                    </HStack>
                  </SortableItem>
                ))}
              </SortableContext>
            </SortableList>
          </div>
        )}
      </div>
      <FilePickerControlledDialog
        isSelectableFilter={props.isSelectableFilter}
        fileDefinitionTypes={[props.baseFilterExtension]}
        getFileSuggestionsContext={getGenomeViewerFileSuggestionsContext}
        baseFilterHint={props.baseFilterHint}
        onValid={handleAddFiles}
        onClose={() => setOpen(false)}
        defaultTab="filters"
        isOpen={open}
        alreadySelectedFiles={props.selectedFiles.map((selectedFile) => selectedFile.file.id)}
      />
    </>
  );
};

export { TrackFilesSelection };
